import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@toki/auth';
import type { SlaPack, SlaPackInput } from '../services/api';
import { createSlaPack, deleteSlaPack, listSlaPacks, SlaPackHasHistoryError, updateSlaPack } from '../services/api';
import { formatMinutes, minutesToInput, parseDuration } from '../config/worklog';
import { FestivosPanel } from './Festivos';
import styles from './Mantenimiento.module.css';

/**
 * PACKS DE SLA (paso 89): cuánto se tarda como mucho en dar la PRIMERA
 * RESPUESTA, por prioridad, y en qué horario corre el reloj.
 *
 * Solo admin (`sla:manage`, D1 de Jose): un pack es parte del contrato. El
 * pack se asigna en la ficha del cliente; quien no tiene pack usa el
 * Estándar, que no se borra ni se desactiva.
 *
 * Editar un pack NO mueve los plazos ya calculados: el vencimiento de un
 * ticket se fija al crearlo (y se recalcula solo si cambia su prioridad).
 * Eso se dice en el formulario, porque es lo que alguien esperaría al revés.
 *
 * Los festivos (paso 89b) son una pestaña de esta misma pantalla: valen para
 * todos los packs. La pestaña no se recuerda: se entra siempre por los packs.
 */

// Un plazo de más de 30 días laborables de 10 h es una errata (mismo techo
// que el backend).
const MAX_PLAZO = 30 * 10 * 60;
const DIAS: [number, string, string][] = [
  [1, 'L', 'lunes'], [2, 'M', 'martes'], [3, 'X', 'miércoles'], [4, 'J', 'jueves'],
  [5, 'V', 'viernes'], [6, 'S', 'sábado'], [7, 'D', 'domingo'],
];
const PRIOS = [
  ['minutosCritica', 'Crítica'], ['minutosAlta', 'Alta'], ['minutosMedia', 'Media'], ['minutosBaja', 'Baja'],
] as const;
type PrioKey = (typeof PRIOS)[number][0];

type Form = {
  nombre: string;
  plazos: Record<PrioKey, string>;
  dias: number[];
  horaInicio: string;
  horaFin: string;
};

const NUEVO: Form = {
  nombre: '',
  plazos: { minutosCritica: '1h', minutosAlta: '2h', minutosMedia: '4h', minutosBaja: '8h' },
  dias: [1, 2, 3, 4, 5],
  horaInicio: '08:00',
  horaFin: '18:00',
};

/** «L-V 8:00–18:00», o los días sueltos si no son seguidos. */
function horario(p: SlaPack): string {
  const d = [...p.dias].sort();
  const seguidos = d.every((x, i) => i === 0 || x === d[i - 1]! + 1);
  const letra = (n: number) => DIAS.find(([k]) => k === n)?.[1] ?? '?';
  const dias = seguidos && d.length > 2 ? `${letra(d[0]!)}-${letra(d[d.length - 1]!)}` : d.map(letra).join(' ');
  return `${dias} ${p.horaInicio}–${p.horaFin}`;
}

export function SlaPacksScreen() {
  const { provider } = useAuth();
  const [packs, setPacks] = useState<SlaPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SlaPack | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>(NUEVO);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SlaPack | null>(null);
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  const [tab, setTab] = useState<'packs' | 'festivos'>('packs');

  const load = useCallback(async () => {
    const token = await provider.getAccessToken();
    if (!token) return;
    try {
      setPacks(await listSlaPacks(token));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => { load(); }, [load]);

  const openForm = (p: SlaPack | null) => {
    setEditing(p);
    setForm(p ? {
      nombre: p.nombre,
      plazos: {
        minutosCritica: minutesToInput(p.minutosCritica),
        minutosAlta: minutesToInput(p.minutosAlta),
        minutosMedia: minutesToInput(p.minutosMedia),
        minutosBaja: minutesToInput(p.minutosBaja),
      },
      dias: [...p.dias],
      horaInicio: p.horaInicio,
      horaFin: p.horaFin,
    } : NUEVO);
    setError(null);
    setOpen(true);
  };

  const plazos = Object.fromEntries(
    PRIOS.map(([k]) => [k, parseDuration(form.plazos[k], MAX_PLAZO)]),
  ) as Record<PrioKey, number | null>;
  const plazoMalo = PRIOS.find(([k]) => plazos[k] === null);
  const horarioMalo = !form.dias.length || form.horaInicio >= form.horaFin;

  const save = async () => {
    const token = await provider.getAccessToken();
    if (!token || plazoMalo || horarioMalo) return;
    setSaving(true);
    setError(null);
    const data: SlaPackInput = {
      nombre: form.nombre.trim(),
      minutosCritica: plazos.minutosCritica!,
      minutosAlta: plazos.minutosAlta!,
      minutosMedia: plazos.minutosMedia!,
      minutosBaja: plazos.minutosBaja!,
      dias: [...form.dias].sort(),
      horaInicio: form.horaInicio,
      horaFin: form.horaFin,
    };
    try {
      if (editing) await updateSlaPack(editing.id, { ...data, isActive: editing.isActive }, token);
      else await createSlaPack(data, token);
      setOpen(false);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (p: SlaPack, isActive: boolean) => {
    const token = await provider.getAccessToken();
    if (!token) return;
    setError(null);
    try {
      await updateSlaPack(p.id, {
        nombre: p.nombre, minutosCritica: p.minutosCritica, minutosAlta: p.minutosAlta,
        minutosMedia: p.minutosMedia, minutosBaja: p.minutosBaja, dias: p.dias,
        horaInicio: p.horaInicio, horaFin: p.horaFin, isActive,
      }, token);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const confirmDelete = async () => {
    const token = await provider.getAccessToken();
    if (!token || !deleteTarget) return;
    setSaving(true);
    setError(null);
    try {
      await deleteSlaPack(deleteTarget.id, token);
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      if (e instanceof SlaPackHasHistoryError) setDeleteBlocked(true);
      else setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleDia = (d: number) =>
    setForm((f) => ({ ...f, dias: f.dias.includes(d) ? f.dias.filter((x) => x !== d) : [...f.dias, d] }));

  if (loading) return <div className={styles.loading}>Cargando…</div>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>SLA</h2>
        {tab === 'packs' && <button className={styles.createBtn} onClick={() => openForm(null)}>+ Nuevo SLA</button>}
      </div>
      <div className={styles.tabs} role="tablist">
        {([['packs', 'Plazos'], ['festivos', 'Festivos']] as const).map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k}
            className={`${styles.tab} ${tab === k ? styles.tabActive : ''}`} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'festivos' ? <FestivosPanel /> : <>
      <p className={styles.muted} style={{ margin: '0 0 14px' }}>
        Cuánto se tarda como mucho en dar la <strong>primera respuesta</strong> al cliente, contando
        solo su horario. Un cliente sin SLA propio usa el Estándar; se cambia en su ficha.
      </p>
      {error && !open && !deleteTarget && <div className={styles.error} style={{ margin: '0 0 12px' }}>{error}</div>}

      <div className={styles.tableCard}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>SLA</th>
              <th>Crítica</th>
              <th>Alta</th>
              <th>Media</th>
              <th>Baja</th>
              <th>Horario</th>
              <th>Clientes</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {packs.map((p) => (
              <tr key={p.id} className={!p.isActive ? styles.inactive : ''}>
                <td>
                  {p.nombre}
                  {p.esEstandar && <span className={styles.sub}>El de todo cliente sin SLA propio</span>}
                </td>
                <td className={styles.num}>{formatMinutes(p.minutosCritica)}</td>
                <td className={styles.num}>{formatMinutes(p.minutosAlta)}</td>
                <td className={styles.num}>{formatMinutes(p.minutosMedia)}</td>
                <td className={styles.num}>{formatMinutes(p.minutosBaja)}</td>
                <td>{horario(p)}</td>
                <td className={styles.num}>{p.clientes}</td>
                <td>
                  <span className={p.isActive ? styles.badgeActive : styles.badgeInactive}>
                    {p.isActive ? 'Activo' : 'Desactivado'}
                  </span>
                </td>
                <td>
                  <div className={styles.actions}>
                    <button className={styles.editBtn} onClick={() => openForm(p)}>Editar</button>
                    {!p.esEstandar && (p.isActive ? (
                      <button className={styles.deactivateBtn} onClick={() => setActive(p, false)}>Desactivar</button>
                    ) : (
                      <button className={styles.editBtn} onClick={() => setActive(p, true)}>Reactivar</button>
                    ))}
                    {!p.esEstandar && (
                      <button className={styles.deactivateBtn} onClick={() => { setError(null); setDeleteBlocked(false); setDeleteTarget(p); }}>
                        Borrar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>}

      {/* ── Crear / editar ───────────────────────────────── */}
      {open && (
        <div className={styles.backdrop} onClick={() => setOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label={editing ? 'Editar SLA' : 'Nuevo SLA'}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{editing ? `Editar ${editing.nombre}` : 'Nuevo SLA'}</h3>
              <button type="button" className={styles.modalClose} onClick={() => setOpen(false)} aria-label="Cerrar">&times;</button>
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.modalBody}>
              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="sla-nombre">Nombre</label>
                <input id="sla-nombre" className={styles.input} value={form.nombre}
                  onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} />
              </div>
              <div className={styles.fieldGroup}>
                <span className={styles.label}>Primera respuesta, como mucho</span>
                <div className={styles.fieldRow}>
                  {PRIOS.map(([k, label]) => (
                    <div key={k} className={styles.fieldGroup}>
                      <label className={styles.label} htmlFor={`sla-${k}`}>{label}</label>
                      <input id={`sla-${k}`} className={styles.input} value={form.plazos[k]}
                        aria-invalid={plazos[k] === null}
                        onChange={(e) => setForm((f) => ({ ...f, plazos: { ...f.plazos, [k]: e.target.value } }))} />
                    </div>
                  ))}
                </div>
                <span className={styles.muted}>
                  En horas de trabajo: «1h», «90», «1h30». {plazoMalo && `No entiendo el plazo de «${plazoMalo[1]}».`}
                </span>
              </div>
              <div className={styles.fieldGroup}>
                <span className={styles.label}>Días en que corre el reloj</span>
                <div className={styles.pillGroup}>
                  {DIAS.map(([n, letra, nombre]) => (
                    <button key={n} type="button" title={nombre} aria-pressed={form.dias.includes(n)}
                      className={`${styles.pill} ${form.dias.includes(n) ? styles.pillActive : ''}`}
                      onClick={() => toggleDia(n)}>
                      {letra}
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.fieldGroup}>
                  <label className={styles.label} htmlFor="sla-ini">Desde</label>
                  <input id="sla-ini" className={styles.input} type="time" value={form.horaInicio}
                    onChange={(e) => setForm((f) => ({ ...f, horaInicio: e.target.value }))} />
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label} htmlFor="sla-fin">Hasta</label>
                  <input id="sla-fin" className={styles.input} type="time" value={form.horaFin}
                    onChange={(e) => setForm((f) => ({ ...f, horaFin: e.target.value }))} />
                </div>
              </div>
              {horarioMalo && (
                <span className={styles.muted}>Elige al menos un día, y que «hasta» sea después de «desde».</span>
              )}
              <span className={styles.muted}>
                Los festivos están en la pestaña «Festivos» y valen para todos los SLA. {editing
                  ? 'Los cambios valen para los tickets que entren a partir de ahora: los abiertos conservan su plazo.'
                  : 'Después, asígnalo a un cliente desde su ficha.'}
              </span>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setOpen(false)}>Cancelar</button>
              <button className={styles.saveBtn} onClick={save}
                disabled={saving || !form.nombre.trim() || !!plazoMalo || horarioMalo}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Borrar (solo sin rastro) ─────────────────────── */}
      {deleteTarget && (
        <div className={styles.backdrop} onClick={() => setDeleteTarget(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Borrar SLA">
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{deleteBlocked ? 'No se puede borrar' : `Borrar ${deleteTarget.nombre}`}</h3>
              <button type="button" className={styles.modalClose} onClick={() => setDeleteTarget(null)} aria-label="Cerrar">&times;</button>
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.modalBody}>
              <p style={{ margin: 0 }}>
                {deleteBlocked
                  ? <>Lo usan clientes o tickets, así que no se puede borrar: se perdería con qué plazo se midieron. {deleteTarget.isActive ? 'Desactívalo: deja de poder asignarse, y quien lo tiene lo sigue usando.' : 'Ya está desactivado.'}</>
                  : <>Solo se puede borrar un SLA que no haya usado nadie. Es <strong>irreversible</strong>.</>}
              </p>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setDeleteTarget(null)}>Cancelar</button>
              {deleteBlocked ? (
                deleteTarget.isActive && (
                  <button className={styles.dangerBtn} onClick={() => { const p = deleteTarget; setDeleteTarget(null); setActive(p, false); }}>
                    Desactivar
                  </button>
                )
              ) : (
                <button className={styles.dangerBtn} onClick={confirmDelete} disabled={saving}>
                  {saving ? 'Borrando…' : 'Borrar'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
